import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ndfApi } from '../../services/ndf-api';
import type { Depense } from '../../types/ndf';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Champ,
  dateFr,
  Etiquette,
  Saisie,
} from './ui/Champ';
import { MessageErreur } from './ui/MessageEtat';
import { erreurGlobale } from './ui/erreurs-champs';
import { VisionneuseJustificatif } from './VisionneuseJustificatif';
import { ApercuJustificatif } from './ApercuJustificatif';
import { ComparaisonDepenses } from './ComparaisonDepenses';
import { useToast } from '../../context/ToastContext';

/**
 * Vérification d'une note, dépense par dépense (specs D6.1).
 *
 * Le justificatif est EN VIS-À-VIS des données, pas sur un autre écran : c'est
 * en comparant les deux qu'on valide, et un aller-retour entre deux pages fait
 * perdre le fil au bout de trois dépenses.
 *
 * Côte à côte sur ordinateur, empilé sur téléphone. Les flèches du clavier
 * avancent et reculent : un valideur qui enchaîne trente dépenses ne veut pas
 * viser un bouton à chaque fois.
 */
export function VerificationNote({
  noteId,
  onFermer,
}: {
  noteId: string;
  onFermer: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [index, setIndex] = useState(0);
  const [motifs, setMotifs] = useState<Record<string, string>>({});
  const [saisieMotif, setSaisieMotif] = useState(false);
  const [motif, setMotif] = useState('');
  const [conclusion, setConclusion] = useState(false);
  const [commentaire, setCommentaire] = useState('');
  const [apercu, setApercu] = useState(false);
  const [comparaison, setComparaison] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['ndf', 'notes', noteId],
    queryFn: () => ndfApi.notes.detail(noteId),
    retry: false,
  });
  const { data: categoriesData } = useQuery({
    queryKey: ['ndf', 'categories'],
    queryFn: () => ndfApi.categories.lire(),
  });

  const depenses = data?.depenses ?? [];
  const courante: Depense | undefined = depenses[index];

  const rafraichir = () => {
    qc.invalidateQueries({ queryKey: ['ndf', 'notes'] });
    qc.invalidateQueries({ queryKey: ['ndf', 'a-valider'] });
  };

  const controler = useMutation({
    mutationFn: (depenseId: string) => ndfApi.notes.marquerControlee(noteId, depenseId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ndf', 'notes', noteId] }),
  });

  const approuver = useMutation({
    mutationFn: () => ndfApi.notes.approuver(noteId),
    onSuccess: () => {
      toast.success('Note approuvée.');
      rafraichir();
      onFermer();
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'L’approbation a échoué.'),
  });

  const rejeter = useMutation({
    mutationFn: () =>
      ndfApi.notes.rejeter(noteId, {
        commentaire: commentaire.trim() || null,
        depenses: Object.entries(motifs).map(([id, m]) => ({ id, motif: m })),
      }),
    onSuccess: () => {
      toast.success('Note renvoyée au collaborateur.');
      rafraichir();
      onFermer();
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'Le refus a échoué.'),
  });

  const aller = (delta: number) =>
    setIndex((i) => Math.min(Math.max(i + delta, 0), Math.max(depenses.length - 1, 0)));

  // Les flèches du clavier : trente dépenses à la suite ne se parcourent pas à
  // la souris. Désactivées pendant une saisie, sinon on navigue en écrivant.
  useEffect(() => {
    const surTouche = (e: KeyboardEvent) => {
      // L'aperçu et la comparaison ont leurs propres raccourcis : naviguer
      // entre les dépenses pendant qu'ils sont ouverts n'aurait aucun sens.
      if (saisieMotif || conclusion || apercu || comparaison) return;
      if (e.key === 'ArrowRight') aller(1);
      if (e.key === 'ArrowLeft') aller(-1);
      if (e.key === 'Escape') onFermer();
    };
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [saisieMotif, conclusion, apercu, comparaison, depenses.length]);

  // Voir une dépense, c'est l'avoir contrôlée : le pointage suit le regard
  // plutôt que de demander un clic de plus à chaque écran.
  useEffect(() => {
    if (courante && data?.note.statut === 'attente_rh' && !courante.controlee_rh_at) {
      controler.mutate(courante.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courante?.id, data?.note.statut]);

  if (isLoading) return <p className="text-sm text-nuit/60">Chargement…</p>;
  if (error) return <MessageErreur erreur={error} />;
  if (!data) return null;

  const { note } = data;
  const pointees = Object.keys(motifs).length;
  const categories = categoriesData?.categories ?? [];
  const categorie = categories.find((x) => x.id === courante?.categorie_id);
  const sousCategorie = categories.find((x) => x.id === courante?.sous_categorie_id);

  if (depenses.length === 0) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-nuit/70">Cette note ne contient aucune dépense.</p>
        <BoutonSecondaire type="button" onClick={onFermer}>
          Retour
        </BoutonSecondaire>
      </div>
    );
  }

  if (comparaison && courante) {
    return (
      <ComparaisonDepenses
        idA={courante.id}
        idB={comparaison}
        onFermer={() => setComparaison(null)}
      />
    );
  }

  return (
    <div className="space-y-4 pb-4">
      {apercu && courante && (
        <ApercuJustificatif
          url={ndfApi.depenses.urlJustificatif(courante.id)}
          onFermer={() => setApercu(false)}
        />
      )}

      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="font-titre text-lg font-bold text-nuit">
            {note.titre || note.numero}
          </h1>
          <p className="text-sm text-nuit/60">
            Dépense {index + 1} sur {depenses.length}
            {pointees > 0 && ` · ${pointees} pointée${pointees > 1 ? 's' : ''}`}
          </p>
        </div>
        <BoutonSecondaire type="button" onClick={onFermer}>
          Fermer
        </BoutonSecondaire>
      </header>

      {!conclusion && courante && (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            {/* Les données à gauche, la pièce à droite : on lit l'une en
                regardant l'autre. */}
            <section className="space-y-2 rounded-lg border border-nuit/10 bg-white p-4 text-sm">
              <h2 className="font-titre font-bold text-nuit">
                {categorie?.libelle ?? 'Dépense'}
                {sousCategorie ? ` · ${sousCategorie.libelle}` : ''}
              </h2>
              <p className="pb-1 text-nuit/60">{dateFr(courante.date_depense)}</p>
              {/* La description dit ce que le montant ne dit pas : « déjeuner
                  client » ou « péage A7 ». Sans elle, le valideur juge un
                  chiffre nu. */}
              <Ligne libelle="Description" valeur={courante.description || '-'} />
              <Ligne
                libelle="Montant TTC"
                valeur={courante.montant_ttc.toLocaleString('fr-FR', {
                  style: 'currency',
                  currency: 'EUR',
                })}
              />
              <Ligne libelle="TVA" valeur={`${courante.montant_tva} €`} />
              {/* R-DEP-10 : sans cette ligne, le valideur voit 48,05 € en face
                  d'un abonnement à 96,10 € et croit à une erreur de saisie. */}
              {categorie?.est_abonnement && courante.quantite !== 1 && (
                <p className="rounded-md bg-ciel/20 p-2 text-nuit">
                  Abonnement mensuel : prise en charge légale de{' '}
                  {Math.round(courante.quantite * 100)} % -{' '}
                  {courante.montant_ttc.toFixed(2).replace('.', ',')} € remboursés sur{' '}
                  {courante.prix_unitaire_ht.toFixed(2).replace('.', ',')} €.
                </p>
              )}
              {courante.commercant && <Ligne libelle="Commerçant" valeur={courante.commercant} />}
              {courante.facturable && (
                <Ligne
                  libelle="Refacturable"
                  valeur={`${courante.client_nom ?? '-'}${courante.mission_nom ? ` · ${courante.mission_nom}` : ''}`}
                />
              )}
              <Ligne
                libelle="Remboursement"
                valeur={courante.demande_remboursement ? 'Demandé' : 'Non (carte entreprise)'}
              />

              {/* Ce qui demande l'attention passe AVANT le reste : c'est pour
                  ça qu'on ouvre cet écran. */}
              {courante.alertes?.length > 0 && (
                <div className="space-y-1 pt-2">
                  {courante.alertes.map((a) => {
                    const autre = typeof a.details?.depense_id === 'string' ? a.details.depense_id : null;
                    return (
                      <div key={a.code} className="rounded-md bg-jaune-fond/20 p-2 text-sm text-jaune">
                        {/* Le message du valideur prime : il dit ce qu'on
                            ENGAGE en approuvant, pas ce qu'il faut corriger. */}
                        <p>{a.messageValideur ?? a.message}</p>
                        {autre && (
                          <button
                            type="button"
                            onClick={() => setComparaison(autre)}
                            className="mt-1 min-h-11 font-semibold text-nuit underline"
                          >
                            Comparer les deux dépenses
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* R-VAL-02 : ce qui avait été pointé au tour précédent. */}
              {courante.deja_en_anomalie && (
                <p className="rounded-md bg-ciel/20 p-2 text-sm text-nuit">
                  Cette dépense avait déjà été pointée lors d’un circuit précédent.
                </p>
              )}

              {motifs[courante.id] && (
                <p className="rounded-md bg-corail-fond/20 p-2 text-sm text-corail">
                  Pointée : {motifs[courante.id]}
                </p>
              )}
            </section>

            <section className="rounded-lg border border-nuit/10 bg-white p-2">
              {courante.a_justificatif === false ? (
                <p className="p-3 text-sm text-nuit/60">Aucun justificatif attaché.</p>
              ) : (
                <VisionneuseJustificatif
                  url={ndfApi.depenses.urlJustificatif(courante.id)}
                  onOuvrir={() => setApercu(true)}
                />
              )}
            </section>
          </div>

          {saisieMotif ? (
            <section className="space-y-2 rounded-lg border border-corail-fond bg-white p-4">
              <Champ label="Pourquoi cette dépense pose problème" requis>
                <Saisie
                  autoFocus
                  value={motif}
                  placeholder="Justificatif illisible, dépense personnelle…"
                  onChange={(e) => setMotif(e.target.value)}
                />
              </Champ>
              <p className="text-xs text-nuit/60">
                Cette raison sera lue par le collaborateur : elle doit lui dire quoi corriger.
              </p>
              <div className="flex flex-wrap gap-2">
                <BoutonSecondaire type="button" onClick={() => setSaisieMotif(false)}>
                  Annuler
                </BoutonSecondaire>
                <BoutonPrincipal
                  type="button"
                  disabled={!motif.trim()}
                  onClick={() => {
                    setMotifs({ ...motifs, [courante.id]: motif.trim() });
                    setMotif('');
                    setSaisieMotif(false);
                    aller(1);
                  }}
                >
                  Pointer cette dépense
                </BoutonPrincipal>
              </div>
            </section>
          ) : (
            <div className="sticky bottom-24 flex flex-wrap items-center gap-2 md:bottom-0">
              <BoutonSecondaire type="button" disabled={index === 0} onClick={() => aller(-1)}>
                ◀ Précédente
              </BoutonSecondaire>
              <BoutonSecondaire
                type="button"
                onClick={() => {
                  setMotif(motifs[courante.id] ?? '');
                  setSaisieMotif(true);
                }}
              >
                Pointer un problème
              </BoutonSecondaire>
              {index < depenses.length - 1 ? (
                <BoutonPrincipal type="button" onClick={() => aller(1)} className="flex-1">
                  Suivante ▶
                </BoutonPrincipal>
              ) : (
                <BoutonPrincipal type="button" onClick={() => setConclusion(true)} className="flex-1">
                  Terminer la vérification
                </BoutonPrincipal>
              )}
            </div>
          )}
        </>
      )}

      {/* Fin de parcours : le bouton proposé DÉPEND de ce qu'on a pointé. Un
          écran qui offrirait les deux laisserait approuver une note dont on
          vient de signaler trois dépenses. */}
      {conclusion && (
        <section className="space-y-3 rounded-lg border border-nuit/10 bg-white p-4">
          <h2 className="font-titre text-sm font-bold text-nuit">
            {pointees === 0
              ? 'Aucun problème signalé sur cette note.'
              : `${pointees} dépense${pointees > 1 ? 's' : ''} pointée${pointees > 1 ? 's' : ''}.`}
          </h2>

          {pointees === 0 ? (
            <>
              <p className="text-sm text-nuit/70">
                En approuvant, la note part {note.statut === 'attente_manager' ? 'au contrôle RH' : 'en remboursement'}.
              </p>
              <div className="flex flex-wrap gap-2">
                <BoutonSecondaire type="button" onClick={() => setConclusion(false)}>
                  Revenir aux dépenses
                </BoutonSecondaire>
                <BoutonPrincipal
                  type="button"
                  disabled={approuver.isPending}
                  onClick={() => approuver.mutate()}
                >
                  {approuver.isPending ? 'Approbation…' : 'Approuver la note'}
                </BoutonPrincipal>
              </div>
            </>
          ) : (
            <>
              <ul className="space-y-1 text-sm">
                {Object.entries(motifs).map(([id, m]) => {
                  const d = depenses.find((x) => x.id === id);
                  return (
                    <li key={id} className="text-corail">
                      • {d?.description || 'Dépense'} : {m}
                    </li>
                  );
                })}
              </ul>
              <Champ label="Commentaire général (facultatif)">
                <Saisie
                  value={commentaire}
                  placeholder="Précision globale…"
                  onChange={(e) => setCommentaire(e.target.value)}
                />
              </Champ>
              <div className="flex flex-wrap gap-2">
                <BoutonSecondaire type="button" onClick={() => setConclusion(false)}>
                  Revenir aux dépenses
                </BoutonSecondaire>
                <button
                  type="button"
                  disabled={rejeter.isPending}
                  onClick={() => rejeter.mutate()}
                  className="min-h-11 flex-1 rounded-lg bg-corail px-4 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {rejeter.isPending ? 'Envoi…' : 'Renvoyer la note au collaborateur'}
                </button>
              </div>
            </>
          )}
        </section>
      )}
    </div>
  );
}

function Ligne({ libelle, valeur }: { libelle: string; valeur: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-nuit/5 pb-1 last:border-0">
      <span className="text-nuit/60">{libelle}</span>
      <span className="text-right font-medium text-nuit">{valeur}</span>
    </div>
  );
}
